import React from 'react';

import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
  createFilterOptions,
} from '@mui/material';

import CustomSelect from 'components/form/CustomSelect';
import { ExpenseLabel, ExpenseStore, createExpenseStore } from 'stores/ExpenseStoreAdapter';
import { errorDefault, successDefault, toErrorDetail } from 'utils/Toastify';

const NAME_MAX = 128;
const INVOICE_MAX = 14;

type StoreOption = ExpenseStore & { inputValue?: string };

const filter = createFilterOptions<StoreOption>();

type Props = {
  stores: ExpenseStore[];
  /** デフォルト品目ラベル指定用の候補 */
  labels: ExpenseLabel[];
  value: string;
  /** 店舗選択時。作成 or 選択された店舗を渡す（defaultLabelId 反映のため） */
  onChange: (store: ExpenseStore | null) => void;
  /** インライン作成した店舗を親の候補一覧に反映する */
  onStoreCreated: (store: ExpenseStore) => void;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
};

/**
 * 店舗の単一セレクト（その場で新規作成可＝createExpenseStore インライン）。
 * 経費登録/編集フォーム用。選択した店舗に defaultLabelId があれば
 * 親側で品目ラベルへ反映する（onChange で store を渡す）。
 * 422（同名重複）・400（defaultLabelId不正）はダイアログ内にインライン表示する。
 */
const ExpenseStoreSelect: React.FC<Props> = ({
  stores,
  labels,
  value,
  onChange,
  onStoreCreated,
  required = false,
  error = false,
  helperText,
  disabled = false,
}) => {
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [newName, setNewName] = React.useState('');
  const [newDefaultLabelId, setNewDefaultLabelId] = React.useState('');
  const [newInvoice, setNewInvoice] = React.useState('');
  const [creating, setCreating] = React.useState(false);
  const [nameError, setNameError] = React.useState<string | undefined>();

  const selected = stores.find((s) => s.expenseStoreId === value) ?? null;
  const labelOptions = labels.map((l) => ({ value: l.expenseLabelId, label: l.name }));

  const openCreateDialog = (presetName: string) => {
    setNewName(presetName);
    setNewDefaultLabelId('');
    setNewInvoice('');
    setNameError(undefined);
    setDialogOpen(true);
  };

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) {
      setNameError('店舗名を入力してください');
      return;
    }
    setCreating(true);
    try {
      const expenseStoreId = await createExpenseStore({
        name,
        ...(newDefaultLabelId ? { defaultLabelId: newDefaultLabelId } : {}),
        ...(newInvoice.trim() ? { invoiceNumber: newInvoice.trim() } : {}),
      });
      const created: ExpenseStore = {
        expenseStoreId,
        name,
        defaultLabelId: newDefaultLabelId || null,
        invoiceNumber: newInvoice.trim() || null,
      };
      onStoreCreated(created);
      onChange(created);
      successDefault('店舗を作成しました');
      setDialogOpen(false);
    } catch (e) {
      const status = (e as { status?: number })?.status;
      if (status === 422) {
        setNameError('同じ名前の店舗が既に存在します');
      } else if (status === 400) {
        errorDefault('デフォルト品目ラベルが不正です', toErrorDetail(e));
      } else {
        errorDefault('店舗の作成に失敗しました', toErrorDetail(e));
      }
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <Autocomplete<StoreOption, false, false, false>
        options={stores as StoreOption[]}
        value={selected as StoreOption | null}
        disabled={disabled}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(o, v) => o.expenseStoreId === v.expenseStoreId}
        onChange={(_, newValue) => {
          if (newValue?.inputValue !== undefined) {
            openCreateDialog(newValue.inputValue ?? '');
            return;
          }
          onChange(newValue ?? null);
        }}
        filterOptions={(options, params) => {
          const filtered = filter(options, params);
          const input = params.inputValue.trim();
          const exists = options.some((o) => o.name.toLowerCase() === input.toLowerCase());
          if (input !== '' && !exists) {
            filtered.push({
              expenseStoreId: `__create__${input}`,
              name: `「${input}」を新規作成`,
              defaultLabelId: null,
              invoiceNumber: null,
              inputValue: input,
            });
          }
          return filtered;
        }}
        renderOption={(optionProps, option) => {
          const { key, ...rest } = optionProps as React.HTMLAttributes<HTMLLIElement> & {
            key: React.Key;
          };
          if (option.inputValue !== undefined) {
            return (
              <Box
                component="li"
                key={String(key)}
                {...rest}
                sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main' }}
              >
                <AddIcon sx={{ fontSize: 18 }} />
                <Typography variant="body2" fontWeight={600}>
                  {option.name}
                </Typography>
              </Box>
            );
          }
          return (
            <Box component="li" key={String(key)} {...rest} sx={{ py: 0.75 }}>
              <Typography variant="body2">{option.name}</Typography>
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="店舗"
            size="small"
            required={required}
            error={error}
            helperText={helperText}
            placeholder="店舗を選択 / 新規作成..."
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
        )}
        noOptionsText="該当する店舗がありません"
        sx={{ width: '100%' }}
      />

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ elevation: 0, sx: { borderRadius: 3, border: 1, borderColor: 'divider' } }}
      >
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            店舗を新規作成
          </Typography>
          <IconButton size="small" onClick={() => setDialogOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="店舗名"
              size="small"
              fullWidth
              required
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              error={!!nameError}
              helperText={nameError}
              inputProps={{ maxLength: NAME_MAX }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <CustomSelect
              mode="single"
              label="デフォルト品目ラベル"
              placeholder="未設定"
              options={labelOptions}
              value={newDefaultLabelId}
              onChange={setNewDefaultLabelId}
            />
            <TextField
              label="インボイス番号"
              size="small"
              fullWidth
              value={newInvoice}
              onChange={(e) => setNewInvoice(e.target.value)}
              inputProps={{ maxLength: INVOICE_MAX }}
              placeholder="T1234567890123"
              helperText="T+13桁（任意）"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDialogOpen(false)}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            キャンセル
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={creating || newName.trim() === ''}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' },
            }}
          >
            作成
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ExpenseStoreSelect;
